import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react'

import type { SortingState } from '@tanstack/react-table'
import { type PaginationState } from '@tanstack/react-table'

import SearchIcon from '@/assets/icons/icon-search.svg'
import { Details } from '@/components/ui/details/details.tsx'
import {
  EmptyProvider,
  EmptySlot,
} from '@/components/ui/empty/empty-provider.tsx'
import { EmptyTableContent } from '@/components/ui/empty/empty-table-content.tsx'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.tsx'
import { DataTable } from '@/components/ui/table/controlled-pagination/DataTable.tsx'
import { TextField } from '@/components/ui/text-field.tsx'
import {
  defineColumns,
  defineDetailsComponents,
} from '@/modules/expiring-fans/components/tables/fans/expiring-fans-table-columns.tsx'
import type { ExpiringFan } from '@/modules/expiring-fans/service/expiring-fans-service.ts'
import { useExpiringFansFilter } from '@/modules/expiring-fans/use-expiring-fans-filter.tsx'
import { useDebouncedCallback } from '@/utils/useDebouncedCallback.ts'

const filterOptions = [
  {
    label: 'All Fans',
    value: 'all',
  },
  {
    label: 'Messaged Fans',
    value: 'messaged',
  },
  {
    label: 'Auto-Messaged Fans',
    value: 'auto-messaged',
  },
  {
    label: 'Unmessaged Fans',
    value: 'not-messaged',
  },
] as const

export type FilterOptionValue = (typeof filterOptions)[number]['value']

export interface ExpiringFansTableProps {
  entries: ExpiringFan[]
  onMarkAsMessaged?: (fan: ExpiringFan) => void
  onMarkAsNotMessaged?: (fan: ExpiringFan) => void
  paginationState: PaginationState
  onPaginationChange: Dispatch<SetStateAction<PaginationState>>
  pageCount: number
  setSearchQuery: Dispatch<SetStateAction<string>>
  searchQuery: string
  sorting: SortingState
  setSorting: Dispatch<SetStateAction<SortingState>>
}

export const ExpiringFansTable = (props: ExpiringFansTableProps) => {
  const {
    entries,
    onMarkAsMessaged,
    onMarkAsNotMessaged,
    paginationState,
    onPaginationChange,
    pageCount,
    setSearchQuery,
    searchQuery,
    sorting,
    setSorting,
  } = props
  const [query, setQuery] = useState(searchQuery)
  const { filter, setFilter } = useExpiringFansFilter()

  const handleResetFilter = useCallback(
    () => {
      setFilter('all')
      setSearchQuery('')
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const renderEmptyFans = useCallback(
    () => (
      <EmptyTableContent className="col-span-12">
        No fans found.{' '}
        {(searchQuery != '' || filter != 'all') && (
          <button
            className="cursor-pointer text-primary-500 hover:underline"
            onClick={handleResetFilter}
          >
            Reset Filters
          </button>
        )}
      </EmptyTableContent>
    ),
    [filter, handleResetFilter, searchQuery],
  )

  const [columns, components] = useMemo(() => {
    const options = { onMarkAsMessaged, onMarkAsNotMessaged }

    return [defineColumns(options), defineDetailsComponents(options)] as const
  }, [onMarkAsMessaged, onMarkAsNotMessaged])

  const setSearchQueryDebounced = useDebouncedCallback(800, setSearchQuery)

  return (
    <div>
      <div className="flex items-center justify-between gap-4 max-md:flex-col max-md:items-start">
        <h3 className="text-2xl font-semibold text-text-default">
          Expiring Fans List
        </h3>
        <div className="flex items-center gap-2">
          <div className="w-full max-w-[200px]">
            <TextField
              prefix={
                <SearchIcon
                  className="flex-shrink-0 text-title"
                  height={14}
                  width={14}
                />
              }
              placeholder="Search"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value)
                setSearchQueryDebounced(event.target.value)
              }}
            />
          </div>
          <div className="h-full w-[200px]">
            <Select
              onValueChange={setFilter}
              value={filter}
            >
              <SelectTrigger className="h-full w-full py-2.5 text-title">
                <SelectValue
                  className="truncate"
                  placeholder="Select Filter"
                />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup className="text-title">
                  {filterOptions.map(({ label, value }) => {
                    return (
                      <SelectItem
                        key={value}
                        value={value}
                      >
                        <span className="truncate">{label}</span>
                      </SelectItem>
                    )
                  })}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
      <EmptyProvider render={renderEmptyFans}>
        <div className="mt-6 hidden lg:block">
          <DataTable
            enableSorting
            enablePagination
            columns={columns}
            data={entries}
            paginationState={paginationState}
            onPaginationChange={onPaginationChange}
            pageCount={pageCount}
            sortingState={sorting}
            onSortingChange={setSorting}
          />
        </div>
        <div className="mt-6 grid grid-cols-12 gap-4 lg:hidden">
          {entries.map((fan) => (
            <Details
              key={fan.fan.id}
              value={fan}
              components={components}
              className="col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3"
            />
          ))}
          {entries.length === 0 && <EmptySlot />}
        </div>
      </EmptyProvider>
    </div>
  )
}
